import type { LayerKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles } from '@festina-life/ui/libs'
import type { SearchMatch } from '@festina-life/ui/search'
import cx from 'classnames'

import type { ConfigurationItem } from '../../types/common'
import { FPlusExpressionViewer } from '../fplusExpression/FPlusExpressionViewer'
import { SimplePath } from '../path/SimplePath'
import { TemplatePath } from '../path/TemplatePath'
import { MatchCategories } from './MatchCategories'
import type { ConfigurationItemSearchMatch, SearchCategoryKey } from './types'

type Props = {
  match: Optional<SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>>
  isShowingCategories: boolean
  operationType: Exclude<ConfigurationItem, 'annulOperation'>
  layer: LayerKey
  block: string
}

export const ConfigurationItemMatchPreview = ({
  match,
  isShowingCategories,
  operationType,
  layer,
  block,
}: Props) => {
  const translate = useTranslate()

  if (!match) {
    return translate('notFound')
  }

  const matchCategories = match.categories

  return (
    <div className="relative flex size-full flex-col gap-y-2">
      {isShowingCategories && <MatchCategories categories={matchCategories} />}
      <div
        className={cx(
          'shrink-0 overflow-y-hidden overflow-x-scroll p-2 italic',
          paperStyles({ isFaded: true }),
        )}
      >
        {['template', 'library'].includes(operationType) ? (
          <TemplatePath
            block={block}
            layer={layer}
            value={match.path}
            variant="normal"
            shouldRenderTemplateId
            isInteractive={false}
          />
        ) : (
          <SimplePath value={match.path} type={operationType} />
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {/* The inner wrapper has no definite height, so the viewer's own
            `size-full` resolves to its content height instead of stretching
            and vertically centering inside the scroll area. */}
        <div className="w-full">
          {match.value.isExpression ? (
            <FPlusExpressionViewer
              isTransparent={false}
              expression={{ fplusSource: match.value.match }}
              path={match.path}
              shouldShowOverflowFallback={false}
              alignment="left"
              isShowingFunctionDeclaration
              type={operationType}
            />
          ) : (
            match.value.match
          )}
        </div>
      </div>
    </div>
  )
}
